import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './Modal.css';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  /**
   * Custom header region rendered INSTEAD of the default title bar — e.g.
   * `ModalBanner`, which sets the title large over an accent band. The banner
   * supplies its own padding and background, so the header wrapper drops both.
   *
   * `title` is still required for accessibility when this is set: with no
   * `.modal-title` element to point at, the dialog falls back to `aria-label`.
   * Everything else (overlay, focus trap, Escape, sizes, body scrolling) is
   * unchanged, so a banner modal is the same component, not a fork.
   */
  banner?: React.ReactNode;
  /**
   * Consulted before an IMPLICIT dismissal (Escape, backdrop click, ×). Return
   * false to refuse — e.g. a dirty form that wants to confirm the discard first.
   * Explicit buttons in the body call their own handlers and bypass this.
   */
  onRequestClose?: () => boolean | void;
  /**
   * false makes every implicit dismissal inert. Use while a destructive action
   * is in flight, so Escape cannot look like it aborted the request.
   */
  dismissible?: boolean;
}

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Open modals, oldest first. Every modal registers a DOCUMENT keydown listener,
 * so without this a stacked pair (a discard confirm over a dirty form) both
 * answered one Escape and the outer handler undid the inner one. Only the top
 * of the stack reacts, and only the last one out restores body scrolling.
 */
const openModals: number[] = [];
let nextModalId = 0;

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'medium',
  banner,
  onRequestClose,
  dismissible = true,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const hasInitialFocusRef = useRef(false);
  const idRef = useRef<number>(-1);
  if (idRef.current === -1) idRef.current = nextModalId++;

  // Escape / backdrop / x. Refused while a destructive action is in flight, or
  // when the consumer's guard says so (unsaved changes).
  const requestClose = useCallback(() => {
    if (!dismissible) return;
    if (onRequestClose && onRequestClose() === false) return;
    onClose();
  }, [dismissible, onRequestClose, onClose]);

  const requestCloseRef = useRef(requestClose);
  useEffect(() => {
    requestCloseRef.current = requestClose;
  }, [requestClose]);

  // Get all focusable elements within the modal
  const getFocusableElements = useCallback((): HTMLElement[] => {
    if (!modalRef.current) return [];
    return Array.from(modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
      .filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
  }, []);

  // Focus trap and escape key handling
  useEffect(() => {
    const id = idRef.current;
    const isTopmost = () => openModals[openModals.length - 1] === id;

    const handleKeyDown = (e: KeyboardEvent) => {
      // A modal underneath must not answer for the one on top.
      if (!isTopmost()) return;

      if (e.key === 'Escape') {
        requestCloseRef.current();
        return;
      }

      if (e.key === 'Tab') {
        const focusableElements = getFocusableElements();
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          // Shift+Tab: if on first element, wrap to last
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab: if on last element, wrap to first
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    if (isOpen) {
      // Store the currently focused element to restore later
      previousActiveElement.current = document.activeElement as HTMLElement;

      openModals.push(id);
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';

      // Focus the first focusable element only on initial open
      if (!hasInitialFocusRef.current) {
        hasInitialFocusRef.current = true;
        requestAnimationFrame(() => {
          const focusableElements = getFocusableElements();
          if (focusableElements.length > 0) {
            focusableElements[0].focus();
          }
        });
      }
    } else {
      hasInitialFocusRef.current = false;
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const at = openModals.indexOf(id);
      if (at !== -1) openModals.splice(at, 1);
      // Only the last modal out gives the page its scrollbar back.
      if (openModals.length === 0) document.body.style.overflow = 'unset';
    };
  }, [isOpen, getFocusableElements]);

  // Restore focus when modal closes
  useEffect(() => {
    if (!isOpen && previousActiveElement.current) {
      previousActiveElement.current.focus();
      previousActiveElement.current = null;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const titleId = 'modal-title';
  const bodyId = 'modal-body';

  return createPortal(
    <div className="modal-overlay" onClick={requestClose}>
      <div
        ref={modalRef}
        className={`modal-content modal-${size}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title && !banner ? titleId : undefined}
        aria-label={banner && title ? title : undefined}
        aria-describedby={bodyId}
      >
        <div className={`modal-header${banner ? ' modal-header--banner' : ''}`}>
          {banner ?? (title && <h2 id={titleId} className="modal-title">{title}</h2>)}
          <button
            className={`modal-close${banner ? ' modal-close--overlay' : ''}`}
            onClick={requestClose}
            disabled={!dismissible}
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>
        <div id={bodyId} className="modal-body">{children}</div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;
